/**
 * normalizeAvatarSrc converts API avatar values into a browser-safe absolute path or URL.
 * Raw storage keys like "avatars/{userId}/{objectId}" must not be used as img src without
 * a leading slash — otherwise the browser resolves them relative to the current route
 * (e.g. /account/profile → /account/avatars/...), which 404s.
 */
export function normalizeAvatarSrc(src: string | undefined | null): string | undefined {
  const toProxyPath = (key: string): string | undefined => {
    const normalizedKey = key.trim().replace(/^\/+/, "");
    if (!normalizedKey || normalizedKey.includes("..")) {
      return undefined;
    }
    return `/api/avatars/${normalizedKey}`;
  };

  const extractAvatarObjectKeyFromAbsoluteURL = (value: string): string | undefined => {
    try {
      const parsed = new URL(value);
      const host = parsed.hostname.toLowerCase();
      const pathname = decodeURIComponent(parsed.pathname);

      // Signed URLs generated from GCS usually follow one of these shapes:
      // - https://storage.googleapis.com/<bucket>/<object-key>
      // - https://<bucket>.storage.googleapis.com/<object-key>
      if (host === "storage.googleapis.com") {
        const parts = pathname.split("/").filter(Boolean);
        if (parts.length >= 2) {
          const objectKey = parts.slice(1).join("/");
          return objectKey.startsWith("avatars/") ? objectKey : undefined;
        }
      }

      if (host.endsWith(".storage.googleapis.com")) {
        const objectKey = pathname.replace(/^\/+/, "");
        return objectKey.startsWith("avatars/") ? objectKey : undefined;
      }

      return undefined;
    } catch {
      return undefined;
    }
  };

  if (src == null) {
    return undefined;
  }
  const s = src.trim();
  if (s === "") {
    return undefined;
  }
  if (s.startsWith("data:")) {
    return s;
  }
  if (s.startsWith("http://") || s.startsWith("https://")) {
    const avatarObjectKey = extractAvatarObjectKeyFromAbsoluteURL(s);
    return avatarObjectKey ? toProxyPath(avatarObjectKey) : s;
  }
  // Legacy broken relative resolution: /account/avatars/{userId}/{id} → proxy path
  if (s.startsWith("/account/avatars/")) {
    const rest = s.slice("/account/avatars/".length);
    return toProxyPath(`avatars/${rest}`);
  }
  if (s.startsWith("/")) {
    return s;
  }
  return toProxyPath(s);
}
